import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share, PlusSquare, X } from "lucide-react";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

let deferredPrompt = null;
const listeners = new Set();
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferredPrompt = e;
  listeners.forEach((l) => l());
});
window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  listeners.forEach((l) => l());
});

export function InstallApp({ compact = false }) {
  const [, force] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);

  if (isStandalone()) return null;

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      deferredPrompt = null;
      force((n) => n + 1);
    } else {
      setShowHelp(true);
    }
  };

  return (
    <>
      <button
        onClick={install}
        data-testid="install-app-button"
        className={`w-full flex items-center justify-center gap-2 border border-[#39FF14]/50 text-[#39FF14] font-bold uppercase tracking-wider hover:bg-[#39FF14] hover:text-black transition-colors ${compact ? "py-2 text-[11px]" : "py-2.5 text-xs"}`}
      >
        <Download size={14} /> Installa l&apos;app sul telefono
      </button>
      {showHelp && createPortal(
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-end sm:place-items-center p-4" onClick={() => setShowHelp(false)} data-testid="install-help-modal">
          <div className="surface-raised w-full max-w-sm p-6 relative fade-up" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowHelp(false)} className="absolute top-3 right-3 text-[#A1A1AA] hover:text-white" data-testid="install-help-close"><X size={18} /></button>
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="GOAL.AI" className="w-12 h-12" />
              <div>
                <div className="heading text-xl font-black">Installa GOAL.AI</div>
                <div className="text-xs text-[#A1A1AA]">L&apos;icona apparirà nella schermata Home</div>
              </div>
            </div>
            {isIOS() ? (
              <ol className="mt-6 space-y-4 text-sm" data-testid="install-help-ios">
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">1</span><span>Apri questa pagina con <b>Safari</b></span></li>
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">2</span><span className="flex items-center gap-1 flex-wrap">Tocca il pulsante <Share size={14} className="inline text-[#007AFF]" /> <b>Condividi</b> in basso</span></li>
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">3</span><span className="flex items-center gap-1 flex-wrap">Scegli <PlusSquare size={14} className="inline" /> <b>Aggiungi alla schermata Home</b>, poi <b>Aggiungi</b></span></li>
              </ol>
            ) : (
              <ol className="mt-6 space-y-4 text-sm" data-testid="install-help-android">
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">1</span><span>Apri questa pagina con <b>Chrome</b></span></li>
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">2</span><span>Tocca il menu <b>⋮</b> in alto a destra</span></li>
                <li className="flex gap-3"><span className="mono text-[#39FF14] font-black">3</span><span>Scegli <b>Installa app</b> o <b>Aggiungi a schermata Home</b></span></li>
              </ol>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
